import { ThemeFontSizeVariable, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import { ClassNames } from "@emotion/react";
import { Fragment, type ReactNode } from "react";
import type { ProseMirrorMark, ProseMirrorNode } from "types/rich-text-field";

interface RichTextRendererProps {
    content: ProseMirrorNode;
}

// Mirrors @caspeco/casper-ui-library v9 proseMirrorContentCss so output matches the casper RichTextRenderer,
// except for links: casper colours them with the venue's primary, which can be the page background, so here
// they take the surrounding text colour and rely on the underline.
// TODO: Change this to the exported rich text renderer when casper is migrated to the latest version
const proseMirrorContentStyles = {
    lineHeight: "1.5",
    "& .ProseMirror > * + *": { marginTop: ThemeSpaceVariable.Small },
    "& .ProseMirror p": { margin: ThemeSpaceVariable.None },
    "& .ProseMirror ul:not([data-type='taskList'])": {
        paddingInlineStart: ThemeSpaceVariable.Medium,
        listStyleType: "disc",
    },
    "& .ProseMirror ol:not([data-type='taskList'])": {
        paddingInlineStart: ThemeSpaceVariable.Medium,
        listStyleType: "decimal",
    },
    "& .ProseMirror a": {
        color: "inherit",
        textDecoration: "underline",
        cursor: "pointer",
    },
    "& .ProseMirror h1": {
        fontSize: ThemeFontSizeVariable.X2Large,
        fontWeight: "bold",
        lineHeight: 1.2,
    },
    "& .ProseMirror h2": {
        fontSize: ThemeFontSizeVariable.XLarge,
        fontWeight: "bold",
        lineHeight: 1.25,
    },
    "& .ProseMirror h3": {
        fontSize: ThemeFontSizeVariable.Large,
        fontWeight: "bold",
        lineHeight: 1.3,
    },
    "& .ProseMirror h4": {
        fontSize: ThemeFontSizeVariable.Medium,
        fontWeight: "bold",
        lineHeight: 1.4,
    },
    "& .ProseMirror strong": { fontWeight: "bold" },
    "& .ProseMirror em": { fontStyle: "italic" },
    "& .ProseMirror u": { textDecoration: "underline" },
};

// Same allow-list as casper's renderer: http(s)/mailto/tel pass through, schemeless URLs are kept,
// anything carrying another scheme (e.g. javascript:) has its href dropped.
const HTTP_MAILTO = /^(https?|mailto):/i;
const TEL = /^tel:\+?[\d\s\-().]{7,}$/i;
const HAS_SCHEME = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;

// A URL parser removes every tab, LF and CR from anywhere inside a URL, and strips leading and
// trailing C0 control characters and spaces, before it resolves the scheme. Testing the authored
// string against these anchored patterns was therefore not an allow-list at all: both
// " javascript:alert(1)" and "java\tscript:alert(1)" passed it and still resolved back to
// javascript: in the browser. Normalise the same way the parser does, and return the normalised
// value so the href that is emitted is the one that was checked.
const STRIPPED_ANYWHERE = /[\t\n\r]/g;

/** Everything up to and including U+0020, i.e. the C0 control characters plus space. */
const IS_TRIMMED = (code: number) => code <= 0x20;

function normalizeHref(raw: string): string {
    const href = raw.replace(STRIPPED_ANYWHERE, "");

    let start = 0;
    let end = href.length;
    while (start < end && IS_TRIMMED(href.charCodeAt(start))) start++;
    while (end > start && IS_TRIMMED(href.charCodeAt(end - 1))) end--;

    return href.slice(start, end);
}

function sanitizeHref(raw: string): string | undefined {
    const href = normalizeHref(raw);
    if (!href) return undefined;
    if (HTTP_MAILTO.test(href) || TEL.test(href)) return href;
    if (HAS_SCHEME.test(href)) return undefined;
    return href;
}

function attrString(attrs: Record<string, unknown> | undefined, key: string): string | undefined {
    const value = attrs?.[key];
    return typeof value === "string" ? value : undefined;
}

function wrapMark(mark: ProseMirrorMark, children: ReactNode): ReactNode {
    switch (mark.type) {
        case "bold":
            return <strong>{children}</strong>;
        case "italic":
            return <em>{children}</em>;
        case "underline":
            return <u>{children}</u>;
        case "link": {
            const href = sanitizeHref(attrString(mark.attrs, "href") ?? "");
            if (!href) return children;
            const target = attrString(mark.attrs, "target") ?? "_blank";
            const rel = attrString(mark.attrs, "rel") || "noopener noreferrer nofollow";
            return (
                <a href={href} target={target} rel={rel}>
                    {children}
                </a>
            );
        }
        default:
            return children;
    }
}

function renderText(node: ProseMirrorNode, key: number): ReactNode {
    const wrapped = (node.marks ?? []).reduce<ReactNode>(
        (children, mark) => wrapMark(mark, children),
        node.text ?? ""
    );
    return <Fragment key={key}>{wrapped}</Fragment>;
}

function renderChildren(node: ProseMirrorNode): ReactNode {
    return node.content?.map((child, i) => renderNode(child, i));
}

function renderNode(node: ProseMirrorNode, key: number): ReactNode {
    switch (node.type) {
        case "doc":
            return <Fragment key={key}>{renderChildren(node)}</Fragment>;
        case "text":
            return renderText(node, key);
        case "hardBreak":
            return <br key={key} />;
        case "paragraph": {
            // Preserve authored blank lines: an empty paragraph renders a <br> so it keeps a line's
            // height, matching how the editor shows it (a bare <p></p> would collapse to zero height).
            const isEmpty = !node.content || node.content.length === 0;
            return <p key={key}>{isEmpty ? <br /> : renderChildren(node)}</p>;
        }
        case "heading": {
            const level =
                typeof node.attrs?.level === "number"
                    ? Math.min(Math.max(node.attrs.level, 1), 4)
                    : 1;
            const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4";
            return <Tag key={key}>{renderChildren(node)}</Tag>;
        }
        case "bulletList":
            return <ul key={key}>{renderChildren(node)}</ul>;
        case "orderedList": {
            const start = node.attrs?.start;
            return (
                <ol key={key} start={typeof start === "number" && start !== 1 ? start : undefined}>
                    {renderChildren(node)}
                </ol>
            );
        }
        case "listItem":
            return <li key={key}>{renderChildren(node)}</li>;
        default:
            return <Fragment key={key}>{renderChildren(node)}</Fragment>;
    }
}

export function RichTextRenderer({ content }: RichTextRendererProps) {
    return (
        <ClassNames>
            {(emotion) => (
                <div className={emotion.css(proseMirrorContentStyles)}>
                    <div className="ProseMirror">{renderNode(content, 0)}</div>
                </div>
            )}
        </ClassNames>
    );
}
